import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { ELEMENT_META, type HypeEggElement } from '@hypevault/games/hypeeggElements';
import { gameSounds } from '../lib/gameSounds';
import { Watermark } from './Watermark';
import { WildAttackFxLayer, type WildFxEntry } from './HypeEggFx';

/**
 * Pass 28ad — HypePets fullscreen panel overlay (variant=panel).
 *
 * A 1920×1080 transparent canvas with a single right-edge slide-in
 * panel that surfaces wild-encounter info. Designed to live as a
 * second OBS browser source alongside the main octagonal widget so
 * streamers running a face-cam layout can keep persistent stats
 * visible without covering the center of the screen.
 *
 * Renders only the wild encounter slice of the engine state — egg,
 * battle, and pet-display flow stay on the primary widget URL.
 */

type HypeEggRarity = 'uncommon' | 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

interface WildParticipant {
  viewer: string;
  damageDealt: number;
  catchAttempted?: boolean;
  caught?: boolean;
  /** Pass 28as — pet HP snapshot persisted by the runner. */
  currentHp?: number;
  maxHp?: number;
  isFainted?: boolean;
}

interface BattleMove {
  i: number;
  attacker: 'a' | 'b';
  damage: number;
  defenderHp: number;
  ko: boolean;
  dodged?: boolean;
  /** Pass 26 — runner pushes move metadata so the overlay can show
   *  "Ember" instead of just a damage number. */
  attackId?: string;
  attackName?: string;
  attackEmoji?: string;
  element?: string;
  effectiveness?: 'super' | 'normal' | 'resisted';
}

interface BattleSide {
  petId: string;
  viewer: string;
  speciesName: string;
  emoji: string;
  imageUrl?: string | null;
  rarity: HypeEggRarity;
  level: number;
  stats: { hp: number; attack: number; defense: number; speed: number };
  isShiny?: boolean;
  nickname?: string | null;
  element?: string | null;
  secondaryElement?: string | null;
}

interface ActiveBattle {
  id: string;
  a: BattleSide;
  b: BattleSide;
  moves: BattleMove[];
  startedAt: number;
  currentMoveIndex: number;
  phase: 'intro' | 'fighting' | 'outcome';
  endsAt: number;
  winnerSide: 'a' | 'b';
  xpWinner: number;
  xpLoser: number;
  gemsAwarded: number;
  streakBonus: number;
  winnerLevelUp: number | null;
  isWild?: boolean;
}

interface ActiveWild {
  id: string;
  rarity: HypeEggRarity;
  species: {
    id: string;
    name: string;
    emoji: string;
    imageUrl?: string | null;
    element?: string | null;
    secondaryElement?: string | null;
  };
  level: number;
  maxHp: number;
  currentHp: number;
  status: 'attacking' | 'catch_phase' | 'concluded';
  spawnedAt: number;
  escapeAt: number;
  catchPhaseEndsAt: number | null;
  participants: WildParticipant[];
  outcome?: 'escaped' | 'no_catchers' | 'all_attempted' | 'time_up';
  recentAttackFx?: WildFxEntry[];
  /** Pass 28ap — active support effects (DoT, stun, HoT, buff) that
   *  the panel surfaces above the HP bar. The engine writes these on
   *  cast + the tick processor prunes expired entries. */
  activeEffects?: {
    dot?: { perTickPct: number; ticksLeft: number; nextTickAt: number };
    stun?: { until: number };
    hot?: { perTickPct: number; ticksLeft: number; nextTickAt: number };
    buffs?: Record<string, { multiplier: number; hitsLeft: number; expiresAt: number }>;
  };
}

interface HypeEggState {
  activeWild?: ActiveWild | null;
  activeBattle?: ActiveBattle | null;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface HypeEggSnapshot {
  state: HypeEggState | null;
  isActive: boolean;
  widgetFields?: {
    sounds?: {
      wild_spawn?: string;
      wild_attack?: string;
      wild_miss?: string;
      wild_heal?: string;
      wild_catch_success?: string;
      wild_catch_fail?: string;
    } | null;
  } | null;
}

const RARITY: Record<
  HypeEggRarity,
  { label: string; frame: string; glow: string; gradient: string }
> = {
  common: {
    label: 'Common',
    frame: '#94a3b8',
    glow: 'rgba(148,163,184,0.55)',
    gradient: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
  },
  uncommon: {
    label: 'Uncommon',
    frame: '#34d399',
    glow: 'rgba(52,211,153,0.6)',
    gradient: 'linear-gradient(180deg, #065f46 0%, #064e3b 100%)',
  },
  rare: {
    label: 'Rare',
    frame: '#60a5fa',
    glow: 'rgba(96,165,250,0.65)',
    gradient: 'linear-gradient(180deg, #1e3a8a 0%, #0c1e4d 100%)',
  },
  epic: {
    label: 'Epic',
    frame: '#c084fc',
    glow: 'rgba(192,132,252,0.7)',
    gradient: 'linear-gradient(180deg, #5b21b6 0%, #2a0d5f 100%)',
  },
  legendary: {
    label: 'Legendary',
    frame: '#fbbf24',
    glow: 'rgba(251,191,36,0.85)',
    gradient: 'linear-gradient(180deg, #b45309 0%, #5a280a 100%)',
  },
  mythic: {
    label: 'Mythic',
    frame: '#fb7185',
    glow: 'rgba(251,113,133,1)',
    gradient: 'linear-gradient(160deg, #be123c 0%, #7c3aed 45%, #0e7490 85%, #ca8a04 100%)',
  },
};

const ANIMATIONS = `
@keyframes hp-slideIn { from { transform: translateX(100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
@keyframes hp-slideOut { from { transform: translateX(0); opacity: 1 } to { transform: translateX(100%); opacity: 0 } }
@keyframes hp-slideInLeft { from { transform: translateX(-100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
@keyframes hp-slideOutLeft { from { transform: translateX(0); opacity: 1 } to { transform: translateX(-100%); opacity: 0 } }
@keyframes hp-koShake { 0%, 100% { transform: translate(0,0) } 25% { transform: translate(-4px, 2px) } 50% { transform: translate(4px, -2px) } 75% { transform: translate(-3px, 3px) } }
@keyframes hp-winnerCrown { 0% { transform: translateY(8px) scale(0.7); opacity: 0 } 60% { transform: translateY(-6px) scale(1.15); opacity: 1 } 100% { transform: translateY(0) scale(1); opacity: 1 } }
@keyframes hp-glow { 0%, 100% { box-shadow: 0 0 24px var(--rarity-glow), 0 0 12px var(--rarity-glow) } 50% { box-shadow: 0 0 48px var(--rarity-glow), 0 0 18px var(--rarity-glow) } }
@keyframes hp-pulse { 0%, 100% { transform: scale(1); opacity: 1 } 50% { transform: scale(1.04); opacity: 0.92 } }
@keyframes hp-fxIn { 0% { transform: translateY(8px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
@keyframes hp-mythicPrism { 0% { background-position: 0% 50% } 100% { background-position: 200% 50% } }
@keyframes hp-artHit { 0%, 100% { transform: scale(1); filter: brightness(1) } 12% { transform: scale(1.06); filter: brightness(1.8) saturate(1.3) } 30% { transform: scale(0.97); filter: brightness(1.3) } 55% { transform: scale(1.02); filter: brightness(1.1) } }
@keyframes hp-panelShake { 0%, 100% { transform: translate(0, 0) } 10%, 50%, 90% { transform: translate(-10px, -3px) } 30%, 70% { transform: translate(10px, 3px) } }
@keyframes hp-panelShakeHard { 0%, 100% { transform: translate(0, 0) } 10%, 50%, 90% { transform: translate(-18px, -6px) } 30%, 70% { transform: translate(18px, 6px) } }
@keyframes hp-healFlash { 0% { background: rgba(52,211,153,0) } 25% { background: rgba(52,211,153,0.32) } 100% { background: rgba(52,211,153,0) } }
@keyframes hp-healFloat { 0% { transform: translate(0, 0); opacity: 0 } 25% { opacity: 1 } 100% { transform: translate(0, -22px); opacity: 0 } }
`;

// Pass 28ae — bumped from 420 to 540 wide; artwork from 84 to 220.
const PANEL_WIDTH = 540;
const PANEL_TOP_MARGIN = 28;
const PANEL_RIGHT_MARGIN = 28;
const ARTWORK_SIZE = 220;
const SLIDE_MS = 480;
const FX_TTL_MS = 6_000;
const PANEL_SHAKE_MS = 450;

export function HypeEggPanelWidget({
  gameStateEvent,
  // Pass 28dl — was declared in the prop TYPE but never destructured,
  // so paid streamers were getting the watermark anyway. Destructure
  // with the existing 'free' default so the gate below actually fires.
  streamerTier = 'free',
}: {
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const [snapshot, setSnapshot] = useState<HypeEggSnapshot | null>(null);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    gameSounds.configure(DEFAULT_GAME_SOUND_CONFIG);
    function unlock() {
      void gameSounds.unlock();
    }
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    bootstrappedRef.current = true;
    const ac = new AbortController();
    fetch(`/api/hypeegg/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: HypeEggSnapshot | null) => {
        if (json) setSnapshot(json);
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const sounds = snapshot?.widgetFields?.sounds;
    if (!sounds) return;
    if (sounds.wild_spawn) gameSounds.registerPackAsset('default', 'wild_spawn', sounds.wild_spawn);
    if (sounds.wild_attack)
      gameSounds.registerPackAsset('default', 'wild_attack', sounds.wild_attack);
    if (sounds.wild_miss) gameSounds.registerPackAsset('default', 'wild_miss', sounds.wild_miss);
    if (sounds.wild_heal) gameSounds.registerPackAsset('default', 'wild_heal', sounds.wild_heal);
    if (sounds.wild_catch_success)
      gameSounds.registerPackAsset('default', 'wild_catch_success', sounds.wild_catch_success);
    if (sounds.wild_catch_fail)
      gameSounds.registerPackAsset('default', 'wild_catch_fail', sounds.wild_catch_fail);
  }, [snapshot?.widgetFields]);

  // Sound triggers — mirror the main widget so the panel-only setup
  // still gets audio cues.
  const prevWildIdRef = useRef<string | null>(null);
  const prevWildHpRef = useRef<number | null>(null);
  const prevCaughtRef = useRef<Set<string>>(new Set());
  const prevAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const wild = snapshot?.state?.activeWild ?? null;
    if (!wild) {
      prevWildIdRef.current = null;
      prevWildHpRef.current = null;
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
      return;
    }
    if (wild.id !== prevWildIdRef.current) {
      void gameSounds.play('wild_spawn');
      prevCaughtRef.current = new Set();
      prevAttemptedRef.current = new Set();
    } else if (prevWildHpRef.current != null && wild.currentHp < prevWildHpRef.current) {
      void gameSounds.play('wild_attack');
    }
    const caughtNow = new Set<string>();
    const attemptedNow = new Set<string>();
    for (const p of wild.participants) {
      if (p.caught) caughtNow.add(p.viewer);
      if (p.catchAttempted) attemptedNow.add(p.viewer);
    }
    for (const v of caughtNow) {
      if (!prevCaughtRef.current.has(v)) void gameSounds.play('wild_catch_success');
    }
    for (const v of attemptedNow) {
      if (!prevAttemptedRef.current.has(v) && !caughtNow.has(v)) {
        void gameSounds.play('wild_catch_fail');
      }
    }
    prevWildIdRef.current = wild.id;
    prevWildHpRef.current = wild.currentHp;
    prevCaughtRef.current = caughtNow;
    prevAttemptedRef.current = attemptedNow;
  }, [snapshot?.state?.activeWild]);

  useEffect(() => {
    if (!gameStateEvent || gameStateEvent.gameType !== 'hypeegg') return;
    setSnapshot((prev) => ({
      state: gameStateEvent.state as HypeEggState | null,
      isActive: gameStateEvent.isActive,
      widgetFields: prev?.widgetFields ?? null,
    }));
  }, [gameStateEvent]);

  const isActive = snapshot?.isActive ?? false;
  const activeWild = isActive ? (snapshot?.state?.activeWild ?? null) : null;
  const activeBattle = isActive ? (snapshot?.state?.activeBattle ?? null) : null;

  // Lingering exit animation: keep a copy of the last wild in state
  // briefly after it clears so the slide-out plays smoothly.
  const [exiting, setExiting] = useState<ActiveWild | null>(null);
  const exitTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (activeWild) {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      setExiting(null);
      return;
    }
    // Wild just cleared — show the previous one fading out.
    setExiting((prev) => prev ?? snapshot?.state?.activeWild ?? null);
    exitTimerRef.current = window.setTimeout(() => {
      setExiting(null);
      exitTimerRef.current = null;
    }, SLIDE_MS);
    return () => {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
    // snapshot ref intentionally omitted — we only want this to fire
    // on transitions of activeWild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWild]);

  // Same lingering exit pattern for battles, so the left-edge slide-
  // out plays after the engine clears activeBattle.
  const [exitingBattle, setExitingBattle] = useState<ActiveBattle | null>(null);
  const battleExitRef = useRef<number | null>(null);
  useEffect(() => {
    if (activeBattle) {
      if (battleExitRef.current != null) {
        window.clearTimeout(battleExitRef.current);
        battleExitRef.current = null;
      }
      setExitingBattle(null);
      return;
    }
    setExitingBattle((prev) => prev ?? snapshot?.state?.activeBattle ?? null);
    battleExitRef.current = window.setTimeout(() => {
      setExitingBattle(null);
      battleExitRef.current = null;
    }, SLIDE_MS);
    return () => {
      if (battleExitRef.current != null) {
        window.clearTimeout(battleExitRef.current);
        battleExitRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBattle]);

  const rendered = activeWild ?? exiting;
  const phase: 'enter' | 'exit' = activeWild ? 'enter' : 'exit';
  const renderedBattle = activeBattle ?? exitingBattle;
  const battlePhase: 'enter' | 'exit' = activeBattle ? 'enter' : 'exit';

  // Panel shake — driven by the shared FX layer's onShake callback.
  // Tracks a single timestamp + tier so re-entry of the same tier
  // restarts the animation via React's keyed remount.
  const [shake, setShake] = useState<{ ts: number; tier: 'shake' | 'shakeHard' } | null>(null);
  const onShake = useCallback((tier: 'shake' | 'shakeHard') => {
    setShake({ ts: Date.now(), tier });
  }, []);
  useEffect(() => {
    if (!shake) return;
    const t = window.setTimeout(() => setShake(null), PANEL_SHAKE_MS);
    return () => window.clearTimeout(t);
  }, [shake]);

  return (
    <>
      <style>{ANIMATIONS}</style>
      {rendered && <WildPanel wild={rendered} phase={phase} shake={shake} />}
      {renderedBattle && <BattlePanel battle={renderedBattle} phase={battlePhase} />}
      <WildAttackFxLayer
        recentFx={activeWild?.recentAttackFx ?? []}
        enableCenterShake={false}
        enableCenterStamp
        onShake={onShake}
      />
      {streamerTier === 'free' && <Watermark />}
    </>
  );
}

function WildPanel({
  wild,
  phase,
  shake,
}: {
  wild: ActiveWild;
  phase: 'enter' | 'exit';
  shake: { ts: number; tier: 'shake' | 'shakeHard' } | null;
}) {
  const rarity = RARITY[wild.rarity] ?? RARITY.common;
  const isMythic = wild.rarity === 'mythic';
  const hpPct = Math.max(0, Math.min(100, (wild.currentHp / Math.max(1, wild.maxHp)) * 100));
  const hpBarColor = hpPct >= 60 ? '#22c55e' : hpPct >= 30 ? '#facc15' : '#f87171';

  const now = useNow(250);
  const escapeMs = Math.max(0, wild.escapeAt - now);
  const catchMs = wild.catchPhaseEndsAt != null ? Math.max(0, wild.catchPhaseEndsAt - now) : 0;
  const escapeSec = Math.ceil(escapeMs / 1000);
  const catchSec = Math.ceil(catchMs / 1000);

  const topAttackers = [...wild.participants]
    .sort((a, b) => b.damageDealt - a.damageDealt)
    .slice(0, 6);

  const recentFx = (wild.recentAttackFx ?? [])
    .filter((f) => now - f.ts < FX_TTL_MS)
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 5);

  // Pass 28ap — most-recent heal event id (group_heal or any
  // group_hot tick). Passed to ParticipantRow so each row keys an
  // animated flash + +HP float off it. The id is stable per fx
  // entry; re-keying restarts the animations.
  const healEvent = (wild.recentAttackFx ?? [])
    .filter(
      (f) =>
        f.outcome === 'support' &&
        (f.supportKind === 'group_heal' || f.supportKind === 'group_hot') &&
        now - f.ts < 1_500,
    )
    .sort((a, b) => b.ts - a.ts)[0];

  const primary = parseElement(wild.species.element);
  const secondary = parseElement(wild.species.secondaryElement);

  // Compose slide-in/out, optional mythic shimmer, and the per-hit
  // panel shake on top of each other. The key prop on the wrapper +
  // shake.ts re-mounts the node so a rapid follow-up hit restarts the
  // shake animation cleanly.
  const shakeAnim = shake
    ? `${shake.tier === 'shakeHard' ? 'hp-panelShakeHard' : 'hp-panelShake'} ${PANEL_SHAKE_MS}ms cubic-bezier(0.36, 0.07, 0.19, 0.97)`
    : '';

  return (
    <div
      key={shake?.ts ?? 'idle'}
      style={
        {
          position: 'fixed',
          top: PANEL_TOP_MARGIN,
          right: PANEL_RIGHT_MARGIN,
          width: PANEL_WIDTH,
          borderRadius: 22,
          border: `2px solid ${rarity.frame}`,
          background: isMythic
            ? rarity.gradient
            : `${rarity.gradient}, radial-gradient(120% 80% at 50% 0%, ${rarity.frame}26 0%, transparent 70%)`,
          backgroundSize: isMythic ? '200% 200%' : undefined,
          animation: [
            `${phase === 'enter' ? 'hp-slideIn' : 'hp-slideOut'} ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
            isMythic ? 'hp-mythicPrism 6s linear infinite' : '',
            shakeAnim,
          ]
            .filter(Boolean)
            .join(', '),
          color: '#f1f5f9',
          fontFamily: '"Inter", system-ui, sans-serif',
          padding: 20,
          boxShadow: `0 0 36px ${rarity.glow}, inset 0 0 0 1px ${rarity.frame}40`,
          '--rarity-glow': rarity.glow,
          pointerEvents: 'none',
        } as React.CSSProperties
      }
    >
      {/* Header — rarity ribbon + status */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            background: `${rarity.frame}26`,
            color: rarity.frame,
            border: `1px solid ${rarity.frame}80`,
            borderRadius: 999,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
          }}
        >
          🌿 Wild · {rarity.label}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color:
              wild.status === 'catch_phase'
                ? '#34d399'
                : wild.status === 'concluded'
                  ? '#94a3b8'
                  : '#fbbf24',
          }}
        >
          {wild.status === 'catch_phase'
            ? `Catch · ${catchSec}s`
            : wild.status === 'concluded'
              ? (wild.outcome ?? 'Resolved')
              : `Attack · ${escapeSec}s`}
        </span>
      </div>

      {/* Hero row — artwork on the left, name/level/types on the
          right. Image still gets the hit-pop animation keyed to
          shake.ts so each new hit restarts the bounce. */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div
          key={`art-${shake?.ts ?? 'idle'}`}
          style={{
            width: ARTWORK_SIZE,
            height: ARTWORK_SIZE,
            borderRadius: 22,
            background: 'rgba(0,0,0,0.35)',
            border: `2px solid ${rarity.frame}90`,
            boxShadow: `0 0 28px ${rarity.glow} inset, 0 0 24px ${rarity.glow}`,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            flexShrink: 0,
            animation: shake
              ? `hp-artHit ${PANEL_SHAKE_MS}ms cubic-bezier(0.36, 0.07, 0.19, 0.97)`
              : undefined,
          }}
        >
          {wild.species.imageUrl ? (
            <img
              src={wild.species.imageUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: 128 }}>{wild.species.emoji ?? '🐾'}</span>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              lineHeight: 1.15,
              textShadow: '0 2px 4px rgba(0,0,0,0.7)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
            }}
          >
            {wild.species.emoji} {wild.species.name}
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: '#cbd5e1',
              fontWeight: 600,
            }}
          >
            Level {wild.level}
          </div>
          {(primary || secondary) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              {primary && <ElementChip element={primary} />}
              {secondary && <ElementChip element={secondary} />}
            </div>
          )}
        </div>
      </div>

      {/* Pass 28ap — active support-effect chips above the HP bar.
          Stun gets its own ribbon for emphasis; DoT / HoT / buff show
          as compact countdown chips. Hidden when no effects are
          live. */}
      {wild.activeEffects && <ActiveEffectsStrip effects={wild.activeEffects} now={now} />}

      {/* HP bar */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: '#cbd5e1',
            marginBottom: 4,
          }}
        >
          <span>HP</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', color: '#f8fafc' }}>
            {Math.max(0, wild.currentHp)} / {wild.maxHp}
          </span>
        </div>
        <div
          style={{
            height: 10,
            background: 'rgba(0,0,0,0.45)',
            borderRadius: 999,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${hpPct}%`,
              background: `linear-gradient(90deg, ${hpBarColor}, ${hpBarColor}cc)`,
              transition: 'width 280ms ease-out',
              boxShadow: `0 0 8px ${hpBarColor}80`,
            }}
          />
        </div>
      </div>

      {/* CTA — what to type */}
      <CallToAction wild={wild} />

      {/* Top attackers */}
      {topAttackers.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SectionLabel>Top damage</SectionLabel>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {topAttackers.map((p, idx) => (
              <ParticipantRow
                key={p.viewer}
                rank={idx + 1}
                participant={p}
                maxHp={wild.maxHp}
                healEventId={healEvent?.id ?? null}
                healKind={
                  healEvent?.supportKind === 'group_heal' || healEvent?.supportKind === 'group_hot'
                    ? healEvent.supportKind
                    : null
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent attack feed */}
      {recentFx.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <SectionLabel>Recent attacks</SectionLabel>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {recentFx.map((f) => (
              <AttackFxRow key={f.id} fx={f} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Pass 28af — battle panel. Mirrors the wild panel format but
 *  slides in from the LEFT edge of the 1920x1080 canvas. Shows both
 *  combatants (image-left / text-right rows), current HPs derived
 *  from the engine's move sequence, the most recent move strip, and
 *  a victory ribbon when phase == outcome. */
function BattlePanel({ battle, phase }: { battle: ActiveBattle; phase: 'enter' | 'exit' }) {
  const now = useNow(250);
  // Choose the panel rarity styling by the higher of the two sides.
  // The panel feels "tier-appropriate" — a Mythic-vs-Common battle
  // gets the mythic prism, not the muted common grey.
  const rarityOrder: HypeEggRarity[] = [
    'common',
    'uncommon',
    'rare',
    'epic',
    'legendary',
    'mythic',
  ];
  const dominantRarity =
    rarityOrder.indexOf(battle.a.rarity) >= rarityOrder.indexOf(battle.b.rarity)
      ? battle.a.rarity
      : battle.b.rarity;
  const rarity = RARITY[dominantRarity] ?? RARITY.common;
  const isMythic = dominantRarity === 'mythic';

  // Derive current HP for each side from the played-back move tape.
  // The engine sets currentMoveIndex to the latest applied move; we
  // walk to that point + read defenderHp on whichever side was the
  // defender of each move.
  const moveCount = battle.moves.length;
  const cursor = Math.max(-1, Math.min(moveCount - 1, battle.currentMoveIndex));
  let aHp = battle.a.stats.hp;
  let bHp = battle.b.stats.hp;
  for (let i = 0; i <= cursor; i++) {
    const m = battle.moves[i];
    if (!m) continue;
    if (m.attacker === 'a') bHp = m.defenderHp;
    else aHp = m.defenderHp;
  }
  aHp = Math.max(0, aHp);
  bHp = Math.max(0, bHp);

  const currentMove = cursor >= 0 ? battle.moves[cursor] : null;
  const remainingMs = Math.max(0, battle.endsAt - now);
  const remainingSec = Math.ceil(remainingMs / 1000);

  const phaseLabel =
    battle.phase === 'intro'
      ? 'Lineup'
      : battle.phase === 'fighting'
        ? `Turn ${Math.max(1, cursor + 1)} / ${moveCount}`
        : 'Result';
  const phaseColor =
    battle.phase === 'outcome' ? '#34d399' : battle.phase === 'fighting' ? '#fbbf24' : '#94a3b8';

  // Last 4 moves for the feed (newest first).
  const recentMoves = battle.moves.slice(Math.max(0, cursor - 3), cursor + 1).reverse();

  return (
    <div
      style={
        {
          position: 'fixed',
          top: PANEL_TOP_MARGIN,
          left: PANEL_RIGHT_MARGIN,
          width: PANEL_WIDTH,
          borderRadius: 22,
          border: `2px solid ${rarity.frame}`,
          background: isMythic
            ? rarity.gradient
            : `${rarity.gradient}, radial-gradient(120% 80% at 50% 0%, ${rarity.frame}26 0%, transparent 70%)`,
          backgroundSize: isMythic ? '200% 200%' : undefined,
          animation: [
            `${phase === 'enter' ? 'hp-slideInLeft' : 'hp-slideOutLeft'} ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
            isMythic ? 'hp-mythicPrism 6s linear infinite' : '',
          ]
            .filter(Boolean)
            .join(', '),
          color: '#f1f5f9',
          fontFamily: '"Inter", system-ui, sans-serif',
          padding: 20,
          boxShadow: `0 0 36px ${rarity.glow}, inset 0 0 0 1px ${rarity.frame}40`,
          '--rarity-glow': rarity.glow,
          pointerEvents: 'none',
        } as React.CSSProperties
      }
    >
      {/* Header — battle ribbon + phase */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <span
          style={{
            background: `${rarity.frame}26`,
            color: rarity.frame,
            border: `1px solid ${rarity.frame}80`,
            borderRadius: 999,
            padding: '3px 12px',
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
        >
          ⚔️ Battle{battle.isWild ? ' · vs Wild' : ''}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.9,
            textTransform: 'uppercase',
            color: phaseColor,
          }}
        >
          {phaseLabel}
          {battle.phase !== 'outcome' && remainingSec > 0 ? ` · ${remainingSec}s` : ''}
        </span>
      </div>

      {/* Side A */}
      <CombatantRow
        side={battle.a}
        currentHp={aHp}
        isWinner={battle.phase === 'outcome' && battle.winnerSide === 'a'}
        rarityFrame={rarity.frame}
        glow={rarity.glow}
        isAttackerOnLastMove={currentMove?.attacker === 'a'}
      />

      {/* VS divider */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          margin: '12px 0',
        }}
      >
        <div
          style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${rarity.frame}80, transparent)`,
          }}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 900,
            letterSpacing: 2,
            color: rarity.frame,
            textShadow: `0 0 8px ${rarity.glow}`,
          }}
        >
          VS
        </span>
        <div
          style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${rarity.frame}80, transparent)`,
          }}
        />
      </div>

      {/* Side B */}
      <CombatantRow
        side={battle.b}
        currentHp={bHp}
        isWinner={battle.phase === 'outcome' && battle.winnerSide === 'b'}
        rarityFrame={rarity.frame}
        glow={rarity.glow}
        isAttackerOnLastMove={currentMove?.attacker === 'b'}
      />

      {/* Current / last move */}
      {currentMove && battle.phase !== 'outcome' && (
        <div
          style={{
            marginTop: 14,
            background: 'rgba(0,0,0,0.35)',
            border: `1px solid ${rarity.frame}40`,
            borderRadius: 10,
            padding: '8px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            animation: 'hp-fxIn 280ms ease-out',
          }}
        >
          <span style={{ fontSize: 18 }}>{currentMove.attackEmoji ?? '💥'}</span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#e2e8f0',
            }}
          >
            {currentMove.attacker === 'a' ? battle.a.viewer : battle.b.viewer}
          </span>
          <span style={{ fontSize: 12, color: '#cbd5e1', flex: 1 }}>
            {currentMove.attackName ?? 'attacks'}
          </span>
          <MoveOutcomeTag move={currentMove} />
        </div>
      )}

      {/* Outcome reward strip */}
      {battle.phase === 'outcome' && (
        <div
          style={{
            marginTop: 14,
            background: 'rgba(52,211,153,0.12)',
            border: '1px solid rgba(52,211,153,0.45)',
            borderRadius: 10,
            padding: '10px 12px',
            animation: 'hp-winnerCrown 380ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: '#a7f3d0',
              marginBottom: 4,
              letterSpacing: 0.5,
            }}
          >
            👑 {battle.winnerSide === 'a' ? battle.a.viewer : battle.b.viewer} wins
            {battle.winnerLevelUp ? ` · LV ${battle.winnerLevelUp}!` : ''}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              fontSize: 11,
              color: '#bbf7d0',
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            <span>+{battle.xpWinner} XP</span>
            {battle.gemsAwarded > 0 && <span>+{battle.gemsAwarded} 💎</span>}
            {battle.streakBonus > 0 && <span>+{battle.streakBonus} streak</span>}
          </div>
        </div>
      )}

      {/* Recent moves feed */}
      {recentMoves.length > 0 && battle.phase !== 'outcome' && (
        <div style={{ marginTop: 12 }}>
          <SectionLabel>Move log</SectionLabel>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {recentMoves.map((m) => (
              <MoveLogRow
                key={`${m.i}`}
                move={m}
                attackerLabel={m.attacker === 'a' ? battle.a.viewer : battle.b.viewer}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CombatantRow({
  side,
  currentHp,
  isWinner,
  rarityFrame,
  glow,
  isAttackerOnLastMove,
}: {
  side: BattleSide;
  currentHp: number;
  isWinner: boolean;
  rarityFrame: string;
  glow: string;
  isAttackerOnLastMove: boolean;
}) {
  const hpPct = Math.max(0, Math.min(100, (currentHp / Math.max(1, side.stats.hp)) * 100));
  const hpBarColor = hpPct >= 60 ? '#22c55e' : hpPct >= 30 ? '#facc15' : '#f87171';
  const isKo = currentHp <= 0;
  const primary = parseElement(side.element);
  const secondary = parseElement(side.secondaryElement);
  const label = side.nickname ? `${side.nickname}` : side.speciesName;

  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        opacity: isKo ? 0.55 : 1,
        animation: isKo ? 'hp-koShake 360ms ease-out' : undefined,
      }}
    >
      <div
        style={{
          width: 110,
          height: 110,
          borderRadius: 18,
          background: 'rgba(0,0,0,0.35)',
          border: `2px solid ${isWinner ? '#34d399' : rarityFrame + '90'}`,
          boxShadow: isWinner
            ? `0 0 18px rgba(52,211,153,0.65)`
            : `0 0 14px ${glow}, 0 0 14px ${glow} inset`,
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          position: 'relative',
          filter: isKo ? 'grayscale(0.85)' : undefined,
        }}
      >
        {side.imageUrl ? (
          <img
            src={side.imageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: 64 }}>{side.emoji ?? '🐾'}</span>
        )}
        {side.isShiny && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: 16,
              filter: 'drop-shadow(0 0 4px rgba(253,224,71,0.8))',
            }}
          >
            ✨
          </span>
        )}
        {isWinner && (
          <span
            style={{
              position: 'absolute',
              top: -10,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: 26,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
              animation: 'hp-winnerCrown 380ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            👑
          </span>
        )}
        {isAttackerOnLastMove && !isKo && !isWinner && (
          <span
            style={{
              position: 'absolute',
              bottom: 2,
              right: 4,
              fontSize: 14,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
            }}
          >
            🎯
          </span>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.15,
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>
            {side.emoji} {label}
          </span>
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            color: '#cbd5e1',
            fontWeight: 600,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span>Lv {side.level}</span>
          <span style={{ opacity: 0.7 }}>·</span>
          <span style={{ fontWeight: 500 }}>@{side.viewer}</span>
        </div>
        {(primary || secondary) && (
          <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
            {primary && <ElementChip element={primary} />}
            {secondary && <ElementChip element={secondary} />}
          </div>
        )}
        {/* HP bar */}
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: '#cbd5e1',
              marginBottom: 2,
            }}
          >
            <span>HP</span>
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#f8fafc' }}>
              {currentHp} / {side.stats.hp}
            </span>
          </div>
          <div
            style={{
              height: 8,
              background: 'rgba(0,0,0,0.45)',
              borderRadius: 999,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${hpPct}%`,
                background: `linear-gradient(90deg, ${hpBarColor}, ${hpBarColor}cc)`,
                transition: 'width 280ms ease-out',
                boxShadow: `0 0 6px ${hpBarColor}80`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MoveOutcomeTag({ move }: { move: BattleMove }) {
  if (move.dodged) {
    return (
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: '#a78bfa',
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        DODGED
      </span>
    );
  }
  if (move.damage <= 0) {
    return (
      <span
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: '#fb923c',
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        MISS
      </span>
    );
  }
  const effColor =
    move.effectiveness === 'super'
      ? '#fde047'
      : move.effectiveness === 'resisted'
        ? '#94a3b8'
        : '#f87171';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: effColor,
        fontFamily: 'ui-monospace, monospace',
      }}
    >
      −{move.damage}
      {move.effectiveness === 'super' ? ' ★' : move.effectiveness === 'resisted' ? ' ↓' : ''}
    </span>
  );
}

function MoveLogRow({ move, attackerLabel }: { move: BattleMove; attackerLabel: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
      }}
    >
      <span style={{ fontSize: 14 }}>{move.attackEmoji ?? '💥'}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 600, flexShrink: 0 }}>{attackerLabel}</span>
      <span
        style={{
          color: '#94a3b8',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {move.attackName ?? 'attacks'}
      </span>
      <MoveOutcomeTag move={move} />
    </div>
  );
}

function CallToAction({ wild }: { wild: ActiveWild }) {
  if (wild.status === 'attacking') {
    return (
      <div
        style={{
          background: 'rgba(251,191,36,0.12)',
          border: '1px solid rgba(251,191,36,0.45)',
          borderRadius: 10,
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          animation: 'hp-pulse 1.8s ease-in-out infinite',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#fef3c7' }}>
          Type <code style={cmdStyle}>!attack</code> to weaken it
        </span>
        <span style={{ fontSize: 18 }}>⚔️</span>
      </div>
    );
  }
  if (wild.status === 'catch_phase') {
    return (
      <div
        style={{
          background: 'rgba(52,211,153,0.12)',
          border: '1px solid rgba(52,211,153,0.5)',
          borderRadius: 10,
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          animation: 'hp-pulse 1.4s ease-in-out infinite',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: '#d1fae5' }}>
          Type <code style={cmdStyle}>!catch</code> — uses one trap
        </span>
        <span style={{ fontSize: 18 }}>🪤</span>
      </div>
    );
  }
  return (
    <div
      style={{
        background: 'rgba(148,163,184,0.1)',
        border: '1px solid rgba(148,163,184,0.35)',
        borderRadius: 10,
        padding: '8px 10px',
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 600,
        color: '#cbd5e1',
      }}
    >
      Encounter resolved
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: '#94a3b8',
      }}
    >
      {children}
    </div>
  );
}

function ParticipantRow({
  rank,
  participant,
  maxHp,
  healEventId,
  healKind,
}: {
  rank: number;
  participant: WildParticipant;
  maxHp: number;
  /** Pass 28ap — id of the most-recent group_heal / group_hot fx
   *  entry. Re-keying the flash overlay on changes restarts the
   *  animation; passing null hides the float. */
  healEventId: string | null;
  healKind: 'group_heal' | 'group_hot' | null;
}) {
  const pct = Math.min(100, (participant.damageDealt / Math.max(1, maxHp)) * 100);
  const isCaught = !!participant.caught;
  const isCatchFail = !!participant.catchAttempted && !participant.caught;
  const isFainted = !!participant.isFainted;
  // Pass 28as — pet HP bar. Falls back to hidden if the runner hasn't
  // pushed the HP snapshot yet (e.g. legacy participant rows).
  const hasHp = participant.maxHp != null && participant.maxHp > 0;
  const hp = participant.currentHp ?? 0;
  const petMaxHp = participant.maxHp ?? 1;
  const hpPct = hasHp ? Math.max(0, Math.min(100, (hp / petMaxHp) * 100)) : 0;
  const hpColor = hpPct > 60 ? '#22c55e' : hpPct > 25 ? '#facc15' : '#f87171';
  const rankColor =
    rank === 1 ? '#fbbf24' : rank === 2 ? '#cbd5e1' : rank === 3 ? '#f97316' : '#64748b';
  const nameColor = isCaught
    ? '#34d399'
    : isCatchFail
      ? '#fb7185'
      : isFainted
        ? '#94a3b8'
        : '#e2e8f0';
  const namePrefix = isCaught ? '🎉 ' : isCatchFail ? '💔 ' : isFainted ? '💀 ' : '';
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
      {/* Pass 28ap — green flash overlay on the row, fired whenever a
          group heal / HoT tick lands. Keyed by event id so a fresh
          event restarts the animation cleanly. */}
      {healEventId && (
        <div
          key={`heal-flash-${healEventId}`}
          style={{
            position: 'absolute',
            inset: -2,
            borderRadius: 6,
            pointerEvents: 'none',
            animation: 'hp-healFlash 700ms ease-out',
          }}
        />
      )}
      {/* Floating +HP indicator off the row's right side. */}
      {healEventId && (
        <span
          key={`heal-float-${healEventId}`}
          style={{
            position: 'absolute',
            right: 6,
            top: -2,
            fontSize: 11,
            fontWeight: 800,
            color: '#34d399',
            textShadow: '0 1px 2px rgba(0,0,0,0.6), 0 0 8px rgba(52,211,153,0.6)',
            pointerEvents: 'none',
            animation: 'hp-healFloat 900ms ease-out forwards',
          }}
        >
          {healKind === 'group_hot' ? '+HP' : '+25% HP'}
        </span>
      )}
      <span
        style={{
          width: 18,
          fontSize: 11,
          fontWeight: 800,
          fontFamily: 'ui-monospace, monospace',
          color: rankColor,
          flexShrink: 0,
        }}
      >
        #{rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: 11,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              color: nameColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 200,
            }}
          >
            {namePrefix}
            {participant.viewer}
          </span>
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              color: '#cbd5e1',
              fontSize: 10,
              flexShrink: 0,
              marginLeft: 6,
            }}
          >
            {participant.damageDealt} dmg
          </span>
        </div>
        {/* Pass 28as.1 — damage contribution bar (relative to wild
            max HP) stays so viewers can see who's pulling weight on
            the kill. */}
        <div
          style={{
            marginTop: 2,
            height: 3,
            background: 'rgba(0,0,0,0.35)',
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: isCaught ? '#34d399' : '#60a5fa',
              transition: 'width 220ms ease-out',
            }}
          />
        </div>
        {/* HP bar with numeric readout. Hidden when the runner
            hasn't pushed an HP snapshot (legacy participant rows). */}
        {hasHp && (
          <>
            <div
              style={{
                marginTop: 3,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 9,
                fontFamily: 'ui-monospace, monospace',
                color: '#94a3b8',
              }}
            >
              <span>HP</span>
              <span>
                {hp} / {petMaxHp}
              </span>
            </div>
            <div
              style={{
                marginTop: 1,
                height: 4,
                background: 'rgba(0,0,0,0.35)',
                borderRadius: 999,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${hpPct}%`,
                  background: isFainted ? '#475569' : hpColor,
                  transition: 'width 220ms ease-out',
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Pass 28ap — compact strip of active support-effect chips above
 *  the wild's HP bar. Stun gets a wider ribbon since it's the most
 *  important state change to surface ("the escape clock is paused"). */
function ActiveEffectsStrip({
  effects,
  now,
}: {
  effects: NonNullable<ActiveWild['activeEffects']>;
  now: number;
}) {
  const chips: React.ReactNode[] = [];
  if (effects.stun) {
    const remaining = Math.max(0, Math.ceil((effects.stun.until - now) / 1000));
    if (remaining > 0) {
      chips.push(
        <span
          key="stun"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'rgba(165,180,252,0.18)',
            border: '1px solid rgba(165,180,252,0.5)',
            color: '#c7d2fe',
            borderRadius: 999,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            animation: 'hp-pulse 1.4s ease-in-out infinite',
          }}
        >
          ❄️ Stunned · {remaining}s
        </span>,
      );
    }
  }
  if (effects.dot && effects.dot.ticksLeft > 0) {
    chips.push(
      <span
        key="dot"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'rgba(248,113,113,0.15)',
          border: '1px solid rgba(248,113,113,0.45)',
          color: '#fca5a5',
          borderRadius: 999,
          padding: '2px 8px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        🔥 DoT · {effects.dot.ticksLeft} left
      </span>,
    );
  }
  if (effects.hot && effects.hot.ticksLeft > 0) {
    chips.push(
      <span
        key="hot"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'rgba(52,211,153,0.15)',
          border: '1px solid rgba(52,211,153,0.45)',
          color: '#6ee7b7',
          borderRadius: 999,
          padding: '2px 8px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}
      >
        🌿 Group HoT · {effects.hot.ticksLeft} left
      </span>,
    );
  }
  if (effects.buffs) {
    const activeBuffCount = Object.values(effects.buffs).filter(
      (b) => b.hitsLeft > 0 && b.expiresAt > now,
    ).length;
    if (activeBuffCount > 0) {
      const sample = Object.values(effects.buffs).find((b) => b.hitsLeft > 0 && b.expiresAt > now);
      const mult = sample ? Math.round((sample.multiplier - 1) * 100) : 0;
      chips.push(
        <span
          key="buff"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'rgba(253,224,71,0.15)',
            border: '1px solid rgba(253,224,71,0.45)',
            color: '#fde68a',
            borderRadius: 999,
            padding: '2px 8px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          🤘 Buff +{mult}% · {activeBuffCount}
        </span>,
      );
    }
  }
  if (chips.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 8,
      }}
    >
      {chips}
    </div>
  );
}

function AttackFxRow({ fx }: { fx: WildFxEntry }) {
  // Pass 28ap — recognize support outcomes so the recent-attacks
  // strip on the panel widget reads "Inferno Brand · DoT" rather
  // than an empty tag.
  const isSupport = fx.outcome === 'support';
  const isCatchFail = fx.outcome === 'catch_fail';
  const supportTag = isSupport
    ? fx.supportKind === 'dot'
      ? `−${fx.damage} DoT`
      : fx.supportKind === 'stun'
        ? 'STUN'
        : fx.supportKind === 'group_heal'
          ? '+HP group'
          : fx.supportKind === 'group_hot'
            ? '+HP / tick'
            : fx.supportKind === 'group_buff'
              ? 'ATK ↑'
              : 'support'
    : '';
  const supportColor =
    fx.supportKind === 'dot'
      ? '#f87171'
      : fx.supportKind === 'stun'
        ? '#a5b4fc'
        : fx.supportKind === 'group_heal' || fx.supportKind === 'group_hot'
          ? '#34d399'
          : '#fde047';
  const color = isCatchFail
    ? '#fb7185'
    : isSupport
      ? supportColor
      : fx.outcome === 'hit'
        ? fx.effectiveness === 'super'
          ? '#fde047'
          : fx.effectiveness === 'resisted'
            ? '#94a3b8'
            : '#f8fafc'
        : fx.outcome === 'miss'
          ? '#94a3b8'
          : '#a78bfa';
  const tag = isCatchFail
    ? 'missed catch'
    : isSupport
      ? supportTag
      : fx.outcome === 'hit'
        ? `−${fx.damage}${fx.effectiveness === 'super' ? ' ★' : fx.effectiveness === 'resisted' ? ' ↓' : ''}`
        : fx.outcome === 'miss'
          ? 'miss'
          : 'dodged';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        animation: 'hp-fxIn 280ms ease-out',
      }}
    >
      <span style={{ fontSize: 14 }}>{fx.attackEmoji}</span>
      <span style={{ color: '#e2e8f0', fontWeight: 600, flexShrink: 0 }}>{fx.attackerViewer}</span>
      <span
        style={{
          color: '#94a3b8',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {fx.attackName}
      </span>
      <span
        style={{ color, fontFamily: 'ui-monospace, monospace', fontWeight: 700, flexShrink: 0 }}
      >
        {tag}
      </span>
    </div>
  );
}

function ElementChip({ element }: { element: HypeEggElement }) {
  const meta = ELEMENT_META[element];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}40`,
        borderRadius: 999,
        padding: '1px 8px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      <span>{meta.emoji}</span>
      <span>{meta.label}</span>
    </span>
  );
}

function parseElement(value: string | null | undefined): HypeEggElement | null {
  if (!value) return null;
  return value in ELEMENT_META ? (value as HypeEggElement) : null;
}

const cmdStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.45)',
  color: '#fef3c7',
  border: '1px solid rgba(251,191,36,0.4)',
  borderRadius: 4,
  padding: '0 5px',
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11,
  fontWeight: 700,
};

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
